import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class StickyAddToCart extends Component {
  connectedCallback() {
    this.addObserver();

    this.addListeners();
  }

  /**
   * Add event listeners for the sticky add to cart, such as click events for closing the sticky or clicking the add to cart button
   */
  addListeners() {
    this.stickyAddToCartButton?.addEventListener('click', this.syncAddToCartButton.bind(this));
    this.stickyQuantityMinusButton?.addEventListener('click', this.syncQuantityMinusButton.bind(this));
    this.stickyQuantityPlusButton?.addEventListener('click', this.syncQuantityPlusButton.bind(this));
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Handle variant update events to refresh the sticky add to cart button
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const newButton = event.detail.data.html.getElementById('sticky-add-to-cart');
    const currentButton = this.querySelector('#sticky-add-to-cart');

    if (currentButton && newButton) {
      morph(currentButton, newButton);
    }
  };

  /**
   * Add observer to toggle sticky class when product form is out of view
   */
  addObserver() {
    const stickyClass = 'sticky-add-to-cart--is-sticky';
    const observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle(stickyClass, !entry.isIntersecting);
    }, {threshold: 0});

    observer.observe(this.productForm);
  }

  /**
   * Close the sticky add to cart when the close button is clicked
   */
  closeStickyAddToCart() {
    this.classList.add('sticky-add-to-cart--hidden');
  }

  /**
   * Handle click events on the sticky add to cart, such as closing the sticky or clicking the add to cart button
   */
  syncAddToCartButton() {
    this.addToCartButton.click();
  }

  /**
   * Handle click events on the sticky add to cart, such as closing the sticky or clicking the add to cart button
   */
  syncQuantityMinusButton() {
    this.quantityMinusButton.click();
  }

  /**
   * Handle click events on the sticky add to cart, such as closing the sticky or clicking the add to cart button
   */
  syncQuantityPlusButton() {
    this.quantityPlusButton.click();
  }

  /**
   * Get the product form element that the sticky add to cart is observing
   * @returns {Element}
   */
  get productForm() {
    return document.querySelector('[data-product-form]');
  }

  /**
   * Get the add to cart button within the sticky add to cart element
   * @returns {Element}
   */
  get stickyAddToCartButton() {
    return this.querySelector('[data-add-to-cart-button]');
  }

  /**
   * Get the quantity minus button within the sticky add to cart element
   * @returns {Element}
   */
  get stickyQuantityMinusButton() {
    return this.querySelector('[data-quantity-minus]');
  }

  /**
   * Get the quantity plus button within the sticky add to cart element
   * @returns {Element}
   */
  get stickyQuantityPlusButton() {
    return this.querySelector('[data-quantity-plus]');
  }

  /**
   * Get the quantity minus button within the sticky add to cart element
   * @returns {Element}
   */
  get quantityMinusButton() {
    return this.productForm.querySelector('[data-quantity-minus]');
  }

  /**
   * Get the quantity plus button within the sticky add to cart element
   * @returns {Element}
   */
  get quantityPlusButton() {
    return this.productForm.querySelector('[data-quantity-plus]');
  }

  /**
   * Get the add to cart button within the product form that the sticky add to cart is syncing with
   * @returns {Element}
   */
  get addToCartButton() {
    return this.productForm.querySelector('[data-add-to-cart-button]');
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}